import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Store, X, CheckCircle2 } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, Empty, Spinner, Chip } from "../components/ui";

const STATUS = ["researching", "applied", "approved", "denied"];
const STATUS_TONE = { researching: "default", applied: "blue", approved: "green", denied: "red" };
const EMPTY = { name: "", category: "Net-30", status: "researching", reporting: false, credit_limit: 0, payment_date: "", notes: "" };

export default function Vendors() {
  const [vendors, setVendors] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/vendors").then(({ data }) => setVendors(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Vendor name is required");
    try {
      await api.post("/vendors", form);
      toast.success("Vendor added");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Could not save vendor"); }
  };

  const update = async (v, patch) => {
    const next = { ...v, ...patch };
    setVendors((list) => list.map((x) => (x.id === v.id ? next : x)));
    try { await api.put(`/vendors/${v.id}`, next); } catch { toast.error("Update failed"); load(); }
  };

  const remove = async (id) => {
    setVendors((list) => list.filter((x) => x.id !== id));
    try { await api.delete(`/vendors/${id}`); toast.success("Vendor removed"); } catch { load(); }
  };

  if (vendors === null) return <Spinner />;

  const reporting = vendors.filter((v) => v.reporting).length;
  const approved = vendors.filter((v) => v.status === "approved").length;

  return (
    <div className="space-y-6">
      <PageHeader
        label="Business Credit"
        title="Net-30 & Vendor Center"
        subtitle="Track vendors, applications, approval and reporting status, and payment dates."
        action={
          <button onClick={() => setOpen(true)} data-testid="add-vendor-btn"
            className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-500">
            <Plus size={16} /> Add Vendor
          </button>
        }
      />

      <div className="grid grid-cols-3 gap-4">
        {[["Total Vendors", vendors.length], ["Approved", approved], ["Reporting", reporting]].map(([l, v]) => (
          <GlassCard key={l} className="p-5">
            <div className="cc-heading text-3xl font-700 text-white">{v}</div>
            <div className="text-sm text-slate-400 mt-1">{l}</div>
          </GlassCard>
        ))}
      </div>

      {vendors.length === 0 ? (
        <GlassCard><Empty icon={Store} title="No vendors yet" subtitle="Add Net-30 vendors and tradelines to track your business credit building.">
          <button onClick={() => setOpen(true)} className="cc-btn-primary rounded-xl px-5 py-2.5 text-sm font-500">Add your first vendor</button>
        </Empty></GlassCard>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {vendors.map((v) => (
            <GlassCard key={v.id} className="p-5" data-testid={`vendor-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="cc-heading text-base font-500 text-white">{v.name}</h3>
                  <span className="text-xs text-slate-500">{v.category}</span>
                </div>
                <button onClick={() => remove(v.id)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`delete-vendor-${v.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {STATUS.map((s) => (
                  <button key={s} onClick={() => update(v, { status: s })}
                    className={`rounded-full px-2.5 py-1 text-xs capitalize transition-colors ${v.status === s ? "cc-gradient text-white" : "border border-white/10 text-slate-400 cc-nav-item"}`}>
                    {s}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <button onClick={() => update(v, { reporting: !v.reporting })}
                  className="flex items-center gap-2 text-sm text-slate-300" data-testid={`vendor-reporting-${v.id}`}>
                  <CheckCircle2 size={16} className={v.reporting ? "text-emerald-400" : "text-slate-600"} />
                  Reports to bureaus
                </button>
                {v.reporting && <Chip tone="green">Reporting</Chip>}
              </div>
              {v.notes && <p className="text-xs text-slate-500 mt-3">{v.notes}</p>}
            </GlassCard>
          ))}
        </div>
      )}

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <motion.div className="absolute inset-0 bg-black/60" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.form onSubmit={save} initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
              className="relative cc-card rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-5">
                <h3 className="cc-heading text-lg font-500 text-white">Add Vendor</h3>
                <button type="button" onClick={() => setOpen(false)} className="text-slate-400 p-1"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <input autoFocus placeholder="Vendor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" data-testid="vendor-name-input" />
                <input placeholder="Category (e.g. Net-30, Tradeline)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" />
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm capitalize">
                  {STATUS.map((s) => <option key={s} value={s} className="bg-[#1a1a24]">{s}</option>)}
                </select>
                <label className="flex items-center gap-2.5 text-sm text-slate-300">
                  <input type="checkbox" checked={form.reporting} onChange={(e) => setForm({ ...form, reporting: e.target.checked })} className="accent-blue-500 h-4 w-4" />
                  Reports to business credit bureaus
                </label>
                <textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" />
              </div>
              <button type="submit" className="cc-btn-primary w-full rounded-xl py-3 text-sm font-500 mt-5" data-testid="save-vendor-btn">Add Vendor</button>
            </motion.form>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
